import { useState, useEffect } from "react";
import FormInput from "../../../../../components/Form/FormInput";
import FormSelect from "../../../../../components/Form/FormSelect";
import FormTextarea from "../../../../../components/Form/TextArea";
import CheckboxField from "../../../../../components/Form/CheckboxField";

const genders = ["Male", "Female", "Other"];

const EditContactPersonForm = ({ person, onCancel, onSave }) => {
  const [formData, setFormData] = useState({ ...person });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setFormData({ ...person });
  }, [person]);

  const requiredFields = ["first_name", "email"];

  const validate = () => {
    const currentErrors = {};
    if (!formData.first_name) currentErrors.first_name = "First Name is required.";
    if (!formData.email) currentErrors.email = "Email is required.";
    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let errorMsg = "";
    if (requiredFields.includes(name) && !value) {
      errorMsg =
        name === "email"
          ? "Email is required."
          : `${name
              .replace("_", " ")
              .replace(/\b\w/g, (c) => c.toUpperCase())} is required.`;
    }
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setFormError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setFormError("");
    setSubmitting(true);
    try {
      await onSave(person.id, formData);
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to update contact person";
      if (/email/i.test(message)) {
        setErrors((prev) => ({ ...prev, email: message }));
      } else {
        setFormError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-bold text-gray-900">Edit Contact Person</h2>
      </div>
      <form
        onSubmit={handleSubmit}
        noValidate
        className="bg-white shadow-sm border border-gray-100 rounded-lg p-5 space-y-5"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 items-start">
          <FormInput
            label="First Name"
            name="first_name"
            value={formData.first_name || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.first_name}
            required
          />

          <FormInput
            label="Last Name"
            name="last_name"
            value={formData.last_name || ""}
            onChange={handleChange}
            onBlur={handleBlur}
          />

          <FormInput
            label="Email"
            name="email"
            type="email"
            value={formData.email || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.email}
            required
          />

          <FormSelect
            label="Gender"
            name="gender"
            value={formData.gender || ""}
            onChange={handleChange}
            options={genders}
          />

          <FormInput
            label="Phone"
            name="phone"
            value={formData.phone || ""}
            onChange={handleChange}
          />

          <FormInput
            label="Teams ID"
            name="teams_id"
            value={formData.teams_id || ""}
            onChange={handleChange}
          />

          <FormInput
            label="Zoom ID"
            name="zoom_id"
            value={formData.zoom_id || ""}
            onChange={handleChange}
          />

          <FormInput
            label="Position"
            name="position"
            value={formData.position || ""}
            onChange={handleChange}
          />

          <div className="md:col-span-2">
            <FormTextarea
              label="Notes"
              name="notes"
              value={formData.notes || ""}
              onChange={handleChange}
              rows={3}
            />
          </div>
        </div>

        <CheckboxField
          label="Is Active"
          name="is_active"
          checked={!!formData.is_active}
          onChange={handleChange}
        />

        <CheckboxField
          label="Is Invoicing"
          name="is_invoicing"
          checked={!!formData.is_invoicing}
          onChange={handleChange}
        />

        {formError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
            {formError}
          </div>
        )}

        <div className="pt-3 border-t border-gray-100 space-y-3">
          <span className="block text-xs text-gray-500">
            Fields marked with <span className="text-red-500">*</span> are mandatory.
          </span>
          <div className="flex gap-2 text-sm">
            <button
              type="submit"
              disabled={submitting}
              className="px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Updating..." : "Update"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="px-3 py-1.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditContactPersonForm;
